export const COUPON_AUDIT_FIELDS = [
  'tipoDescuento',
  'descuentoCompra',
  'descuentoEnvio',
  'cantidadDeCanjesPorUsuario',
  'cantidadDeCanjesTotal',
  'fechaInicio',
  'fechaFin',
  'minimoDeCompra',
  'sucursal',
]

export const COUPON_AUDIT_FIELD_LABELS = {
  nombre: 'Nombre',
  tipoDescuento: 'Tipo de descuento',
  descuentoCompra: 'Descuento compra',
  descuentoEnvio: 'Descuento envío',
  cantidadDeCanjesPorUsuario: 'Canjes por usuario',
  cantidadDeCanjesTotal: 'Canjes totales',
  fechaInicio: 'Fecha inicio',
  fechaFin: 'Fecha fin',
  minimoDeCompra: 'Mínimo de compra',
  sucursal: 'Sucursal',
}

export const COUPON_AUDIT_ACTION_LABELS = {
  created: 'Creación',
  updated: 'Actualización',
  renamed: 'Renombre',
  product_added: 'Producto agregado',
  product_removed: 'Producto eliminado',
}

/**
 * @param {Object} actor - Usuario autenticado (Vuex currentUser)
 * @returns {{ uid: string, email: string, nombre: string }}
 */
export const buildCouponActor = actor => ({
  uid: actor.uid,
  email: actor.email || '',
  nombre: actor.nombre || actor.name || actor.email || actor.uid,
})

/**
 * @param {Object|null|undefined} actor
 * @returns {{ valid: boolean, actor?: Object, error?: string }}
 */
export const validateCouponActor = actor => {
  if (!actor || !actor.uid) {
    return {
      valid: false,
      error: 'No se pudo identificar al usuario. Vuelva a iniciar sesión.',
    }
  }

  return {
    valid: true,
    actor: buildCouponActor(actor),
  }
}

/**
 * Normaliza campos del cupón para comparación y snapshot de auditoría.
 * @param {Object} data
 * @returns {Object}
 */
export const normalizeCouponForAudit = data => ({
  tipoDescuento: data.tipoDescuento || 'carrito',
  descuentoCompra: String(data.descuentoCompra ?? '0'),
  descuentoEnvio: String(data.descuentoEnvio ?? '0'),
  cantidadDeCanjesPorUsuario: String(data.cantidadDeCanjesPorUsuario ?? '0'),
  cantidadDeCanjesTotal: String(data.cantidadDeCanjesTotal ?? '0'),
  fechaInicio: String(data.fechaInicio ?? ''),
  fechaFin: String(data.fechaFin ?? ''),
  minimoDeCompra: String(data.minimoDeCompra ?? '0'),
  sucursal: String(data.sucursal ?? '0'),
})

/**
 * @param {Object} before - Estado normalizado anterior
 * @param {Object} after - Estado normalizado nuevo
 * @returns {Object} Mapa campo -> { from, to }
 */
export const computeCouponChanges = (before, after) => {
  const changes = {}

  COUPON_AUDIT_FIELDS.forEach(field => {
    const from = before[field]
    const to = after[field]
    if (from !== to) {
      changes[field] = { from, to }
    }
  })

  return changes
}

/**
 * @param {Object|null|undefined} product
 * @returns {string|null}
 */
export const extractProductCode = product => {
  if (!product) return null

  return (
    product.CODIGO ||
    product.Data?.CODIGO ||
    product.ProductoID ||
    product.Data?.ProductoID ||
    product.objectID ||
    product.Data?.objectID ||
    null
  )
}

/**
 * @param {Object|null|undefined} product
 * @returns {{ codigo: string, nombre: string }}
 */
export const extractProductAuditInfo = product => {
  const codigo = extractProductCode(product)
  if (!codigo) {
    return { codigo: '', nombre: 'Producto sin código' }
  }

  const data = product.Data || product
  const nombre =
    data.DESCRIPCION ||
    data.descripcion ||
    data.Descripcion ||
    data.nombre ||
    data.Nombre ||
    data.name ||
    data.Name ||
    codigo

  return { codigo: String(codigo), nombre: String(nombre) }
}

/**
 * @param {'product_added'|'product_removed'} action
 * @param {Object} actor
 * @param {Object} productInfo - { codigo, nombre }
 * @param {number} [timestamp]
 * @returns {Object}
 */
export const buildProductAuditEntry = (action, actor, productInfo, timestamp = Date.now()) => ({
  action,
  at: timestamp,
  by: actor,
  product: {
    codigo: productInfo.codigo,
    nombre: productInfo.nombre || productInfo.codigo,
  },
})

/**
 * @param {'created'|'updated'|'renamed'|'product_added'|'product_removed'} action
 * @param {Object} actor
 * @param {Object|null} changes
 * @param {Object|null} snapshot
 * @param {number} [timestamp]
 * @returns {Object}
 */
export const buildAuditEntry = (action, actor, changes = null, snapshot = null, timestamp = Date.now()) => {
  const entry = {
    action,
    at: timestamp,
    by: actor,
  }

  if (changes && Object.keys(changes).length > 0) {
    entry.changes = changes
  }

  if (snapshot) {
    entry.snapshot = snapshot
  }

  return entry
}

/**
 * Construye el objeto de cambios para un renombre (incluye otros campos del formulario si aplican).
 * @param {string} oldName
 * @param {string} newName
 * @param {Object} before - Estado normalizado anterior
 * @param {Object} after - Estado normalizado nuevo
 * @returns {Object}
 */
export const buildRenameChanges = (oldName, newName, before, after) => ({
  nombre: { from: oldName, to: newName },
  ...computeCouponChanges(before, after),
})

/**
 * @param {Object|null|undefined} auditLog
 * @returns {Array<Object>}
 */
export const getSortedAuditLogEntries = auditLog => {
  if (!auditLog || typeof auditLog !== 'object') {
    return []
  }

  return Object.entries(auditLog)
    .map(([id, entry]) => ({ id, ...entry }))
    .sort((a, b) => (b.at || 0) - (a.at || 0))
}

/**
 * @param {Object|null|undefined} actor
 * @returns {string}
 */
export const formatCouponActor = actor => {
  if (!actor) return 'N/A'
  return actor.nombre || actor.email || actor.uid || 'N/A'
}
